import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { TextField, Typography, Grid, InputAdornment, Button, Box, Autocomplete } from '@mui/material';
import axiosInstance from '../../utils/axios';
import { objectToFormData } from '../../utils/objectToFormData';


IncoiceAddProductForm.propTypes = {
  open: PropTypes.bool,
  close: PropTypes.func,
  vendorID: PropTypes.number,
  invoiceProductNode: PropTypes.shape({
    data: PropTypes.shape({
      description: PropTypes.string,
      vendorItemId: PropTypes.string,
      uomType: PropTypes.string,
      price: PropTypes.number,
    }),
  }),
  setInvoiceItems: PropTypes.func,
};

export default function IncoiceAddProductForm({ invoiceProductNode, setInvoiceItems, open, close, vendorID }) {
  const { enqueueSnackbar } = useSnackbar();
  const [categories, setCategories] = useState([]);
  const [formValue, setFormValue] = useState({
    vendorId: 0,
    name: '',
    description: '',
    vendorItemId: '',
    productCategory: '',
    uomType: '',
    price: 0,
  });

  useEffect(() => {
    if (open && invoiceProductNode?.data) {
      setFormValue({
        vendorId: vendorID ?? 0,
        name: invoiceProductNode.data.description ?? '',
        description: '',
        vendorItemId: invoiceProductNode.data.vendorItemId ?? '',
        productCategory: '',
        uomType: invoiceProductNode.data.uomType ?? '',
        price: invoiceProductNode.data.price ?? 0,
      });
      axiosInstance.get("/productCategory/productCategories")
        .then(res => {
          setCategories(res.data.map((cat) => ({ label: cat.name, value: cat.id })));
        })
        .catch(() => {
          enqueueSnackbar('Failed to fetch product categories', { variant: 'error' });
        });
    }
  }, [open, invoiceProductNode]);

  const submit = async () => {
    try {
      const newProduct = {
        vendorId: formValue.vendorId,
        name: formValue.name,
        description: formValue.description,
        vendorItemId: formValue.vendorItemId,
        productCategoryId: formValue.productCategory,
        uomTypes: [
          {
            uomType: formValue.uomType,
            price: formValue.price,
            uomAmount: 1,
          },
        ],
      };
      const formData = objectToFormData(newProduct);
      const response = await axiosInstance.post('/product/productForm', formData);
      if (response.data) {
        enqueueSnackbar('Item added to catalog successfully', { variant: 'success' });
        setInvoiceItems((prevItems) =>
          prevItems.map((item) =>
            item.vendorItemId === formValue.vendorItemId ? { ...item, needsToBeAdded: false } : item
          )
        );
        close();
      } else {
        enqueueSnackbar('Error saving product', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Error saving product', { variant: 'error' });
    }
  };

  return (
    <Box>
      <Typography sx={{ textAlign: "center", mb: 2 }}>Add Item</Typography>
      <Grid container xl={12} spacing={2}>
        <Grid item xl={6}>
          <Typography variant="subtitle1">Product Name*</Typography>
          <TextField
            name="name"
            size="small"
            placeholder="Enter Product Name"
            fullWidth
            value={formValue.name}
            onChange={(e) => setFormValue({ ...formValue, name: e.target.value })}
          />
        </Grid>
        <Grid item xl={6}>
          <Typography variant="subtitle1">Description</Typography>
          <TextField
            name="description"
            size="small"
            placeholder="Enter Description"
            fullWidth
            value={formValue.description}
            onChange={(e) => setFormValue({ ...formValue, description: e.target.value })}
          />
        </Grid>
        <Grid item xl={6}>
          <Typography variant="subtitle1">Vendor Item ID*</Typography>
          <TextField
            name="vendorItemId"
            size="small"
            placeholder="Enter Vendor Item ID"
            fullWidth
            value={formValue.vendorItemId}
            onChange={(e) => setFormValue({ ...formValue, vendorItemId: e.target.value })}
          />
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle1">Category*</Typography>
          <Autocomplete
            fullWidth
            size="small"
            value={categories.find((category) => category.value === formValue.productCategory) || null}
            onChange={(event, val) => setFormValue((prev) => ({ ...prev, productCategory: val?.value ?? "" }))}
            options={categories}
            renderInput={(params) => <TextField {...params} placeholder="Select Category" />}
          />
        </Grid>
        <Grid item xl={6}>
          <Typography variant="subtitle1">UOM*</Typography>
          <TextField
            name="uomType"
            size="small"
            placeholder="Enter UOM"
            fullWidth
            value={formValue.uomType}
            onChange={(e) => setFormValue({ ...formValue, uomType: e.target.value })}
          />
        </Grid>
        <Grid item xl={6}>
          <Typography variant="subtitle1">Price*</Typography>
          <TextField
            name="price"
            size="small"
            placeholder="Enter Price"
            fullWidth
            value={(formValue.price ?? 0).toFixed(2)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              inputComponent: CurrencyInput,
            }}
            inputProps={{ onValueChange: (value) => setFormValue({ ...formValue, price: Number(value ?? 0) }) }}
          />
        </Grid>
        <Grid item xl={12} display="flex" justifyContent="center" mb={2}>
          <Button
            disabled={!formValue.name || !formValue.vendorItemId || !formValue.productCategory || !formValue.price > 0}
            sx={{ px: 7, mr: 2 }}
            color="purple"
            size="medium"
            variant="contained"
            onClick={submit}
          >
            Save
          </Button>
          <Button sx={{ px: 6 }} color="error" size="medium" variant="outlined" onClick={close}>
            Cancel
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

